import React, { useEffect, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { ReactComponent as LoadingVehicleIcon } from 'src/Images/icons/vehicle-tracking/create-delivery-empty-state.svg';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as PrintIcon } from 'src/Images/icons/common/print.svg';
import { Drawer, useMediaQuery } from '@mui/material';
import PrintCameraWeightReport from 'src/components/WeightReport/PrintCameraWeightReport';
import VehicleTrackingOutwardLoadDetails from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingOutwardLoadDetails';
import { ReactComponent as LoadingLockIcon } from 'src/Images/icons/vehicle-planning/loading-locked.svg';
import VehicleTrackingManageTareWeight from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingManageTareWeight';
import VehicleTrackingManageGrossWeight from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingManageGrossWeight';
import VehicleTrackingConfirmLoading from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingConfirmLoading';
import VehicleTrackingLoadEditComponent from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingLoadEditComponent';
import VehicleTrackingCreateLoad from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingCreateLoad';
import VehicleTrackingCompletedLoadOverview from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingCompletedLoadOverview';
import { ReactComponent as TargetInfoIcon } from 'src/Images/icons/common/target-info-icon.svg';
import { getVehicleTrackingAttachedLoads } from 'src/redux/Actions/VehicleTrackingOutward';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import getColorCode from 'src/utils/GetThemeColor';

function VariancePill({ value }) {
  if (value === null) return null;
  return (
    <span className={`text-sm status-pill ${value >= 0 ? 'green' : 'red'}`}>
      {value > 0 ? '+' : ''}
      {value}% Variance
    </span>
  );
}

const LOAD_STATUS = {
  TARE_WEIGHT: 1,
  CONFIRM_LOADING: 2,
  GROSS_WEIGHT: 3,
};

export default function VehicleTrackingLoadTab({ createLoadTabActive }) {
  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { vehicleTrackingDetails } = vehicleTrackingReducer;

  const isMobile = useMediaQuery('(max-width:600px)');

  const themeColor = authReducer?.userData?.tenant?.theme;
  const [selectedLoadId, setSelectedLoadId] = useState(null);
  // Tracks the load currently opened in the "Edit" screen (VehicleTrackingLoadEditComponent),
  // kept separate from selectedLoadId which drives the Proceed/status flow.
  const [showCreateLoad, setShowCreateLoad] = useState(false);
  const [selectedLoadObjForEdit, setSelectedLoadObjForEdit] = useState(null);
  const { vehicleTrackingAttachedLoads } = VehicleTrackingOutwardReducer;

  const [showLoadDetails, setShowLoadDetails] = useState(false);
  const [vtLoadHeaderUniqueNumber, setVtLoadHeaderUniqueNumber] =
    useState(null);
  const [showLoadCameraWeightReport, setShowLoadCameraWeightReport] =
    useState(false);
  const [
    cameraReportLoadHeaderUniqueNumber,
    setCameraReportLoadHeaderUniqueNumber,
  ] = useState(null);
  const [isLoadEditing, setIsLoadEditing] = useState(false);
  const { vehicleTrackingNumber } = useParams();
  const tenantOrganisationId = authReducer.userData.organisations?.id;

  const allowLoadTransaction =
    vehicleTrackingDetails?.requestedStage?.allow_load_transaction;
  const canEditConfirmedLoad = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.EDIT_CONFIRMED_LOAD,
  );

  const vehicleTrackingLoadData =
    vehicleTrackingAttachedLoads?.vehicleTrackingLoadData;

  const requestedStage = vehicleTrackingDetails?.requestedStage;
  const requestedStageEntry = requestedStage?.entry_log_exist;

  const toggleLoadDetailDrawer = () => {
    setShowLoadDetails((prev) => !prev);
  };

  const handleCreateLoadClick = () => {
    setShowCreateLoad(true);
  };

  const handleBackFromCreateLoad = () => {
    setShowCreateLoad(false);
  };

  useEffect(() => {
    if (selectedLoadId !== null) return;

    dispatch(
      getVehicleTrackingAttachedLoads(
        tenantOrganisationId,
        vehicleTrackingNumber,
      ),
    );
  }, [dispatch, selectedLoadId]);

  useEffect(() => {
    if (!createLoadTabActive) return;

    setSelectedLoadId(null);
    setSelectedLoadObjForEdit(null);
    setShowCreateLoad(true);
  }, [createLoadTabActive]);

  const calcVariance = (planned, actual) => {
    const p = Number(planned);
    const a = Number(actual);
    const isValid = p > 0 && actual != null && Number.isFinite(a);
    return isValid ? Number((((a - p) / p) * 100).toFixed(1)) : null;
  };

  const handleProceedClick = (loadObj) => {
    setSelectedLoadId(loadObj?.id ?? null);
  };

  const handleBackFromManageScreen = () => {
    setSelectedLoadId(null);
  };

  // Opens the VehicleTrackingLoadEditComponent screen for the given load.
  const handleEditLoadClick = (loadObj) => {
    setSelectedLoadObjForEdit(loadObj);
  };

  const handleLoadCameraReportClick = (loadObj) => {
    setCameraReportLoadHeaderUniqueNumber(
      loadObj?.vt_load_header_unique_number,
    );
    setShowLoadCameraWeightReport(true);
  };

  const handleCloseLoadCameraReport = () => {
    setShowLoadCameraWeightReport(false);
    setCameraReportLoadHeaderUniqueNumber(null);
  };

  const handleBackFromEditScreen = () => {
    setSelectedLoadObjForEdit(null);
  };

  if (showCreateLoad) {
    return (
      <VehicleTrackingCreateLoad
        onBack={handleBackFromCreateLoad}
        themeColor={themeColor}
      />
    );
  }

  // Edit screen takes priority: if a load is being edited via the "Edit" button,
  // render VehicleTrackingLoadEditComponent instead of the status-driven flow below.
  if (selectedLoadObjForEdit !== null) {
    return (
      <VehicleTrackingLoadEditComponent
        onBack={handleBackFromEditScreen}
        themeColor={themeColor}
        vtLoadHeaderUniqueNumber={
          selectedLoadObjForEdit?.vt_load_header_unique_number
        }
        selectedLoad={selectedLoadObjForEdit}
      />
    );
  }

  if (selectedLoadId !== null) {
    const selectedLoad = vehicleTrackingLoadData?.find(
      (loadObj) => loadObj?.id === selectedLoadId,
    );
    const selectedLoadStatusId = selectedLoad?.status?.id;
    const selectedLoadHeaderUniqueNumber =
      selectedLoad?.vt_load_header_unique_number;

    if (selectedLoad?.completed) {
      return (
        <VehicleTrackingCompletedLoadOverview
          onBack={handleBackFromManageScreen}
          themeColor={themeColor}
          vtLoadHeaderUniqueNumber={selectedLoadHeaderUniqueNumber}
          selectedLoad={selectedLoad}
        />
      );
    }

    // Status 1 -> Tare Weight
    if (selectedLoadStatusId === LOAD_STATUS.TARE_WEIGHT) {
      return (
        <VehicleTrackingManageTareWeight
          onBack={handleBackFromManageScreen}
          themeColor={themeColor}
          vtLoadHeaderUniqueNumber={selectedLoadHeaderUniqueNumber}
        />
      );
    }

    // Status 2 -> Confirm Loading
    if (selectedLoadStatusId === LOAD_STATUS.CONFIRM_LOADING) {
      return (
        <VehicleTrackingConfirmLoading
          onBack={handleBackFromManageScreen}
          themeColor={themeColor}
          vtLoadHeaderUniqueNumber={selectedLoadHeaderUniqueNumber}
        />
      );
    }

    // Status 3 -> Gross Weight
    if (selectedLoadStatusId === LOAD_STATUS.GROSS_WEIGHT) {
      return (
        <VehicleTrackingManageGrossWeight
          onBack={handleBackFromManageScreen}
          themeColor={themeColor}
          vtLoadHeaderUniqueNumber={selectedLoadHeaderUniqueNumber}
        />
      );
    }
  }

  return (
    <div className="h-100 div-flex-column">
      {vehicleTrackingLoadData?.length === 0 ? (
        <div className="h-100 div-flex-column div-flex-center div-align-center">
          <LoadingVehicleIcon />
          <span className="text-grey text-xxl text-semi-bold">Create Load</span>
          <span className="text-grey text-sm">
            Plan your delivery by making loads.
          </span>
          <span className="text-grey text-sm">
            You can start by creating one or multiple loads.
          </span>
          <div className="div-flex-row py-3">
            <button
              className={`common-btn theme-background-${themeColor}`}
              onClick={handleCreateLoadClick}
            >
              Create Load
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`loading-station-tab-container setting-right-side-container theme-scrollable-container-${themeColor}`}
        >
          {allowLoadTransaction ? (
            <Row className="g-3">
              {vehicleTrackingLoadData?.map((loadObj) => (
                <Col xxl={3} xl={4} lg={6} key={loadObj?.id}>
                  <div className="">
                    <div className="loading-component-container-locked div-flex-column rg-10 pb-2">
                      {loadObj?.is_locked && (
                        <div className="loading-locked-overlay">
                          <LoadingLockIcon className="loading-lock-icon" />
                          <div className="div-flex-row div-align-center cg-5">
                            <button
                              className="icon-btn-none"
                              aria-label="Camera Report"
                              title="Camera Report"
                              onClick={() =>
                                handleLoadCameraReportClick(loadObj)
                              }
                            >
                              <PrintIcon
                                color={getColorCode(themeColor)}
                                className="icon-size-xxxl"
                              />
                            </button>
                            <button
                              className="icon-btn-none"
                              onClick={() => {
                                setVtLoadHeaderUniqueNumber(
                                  loadObj?.vt_load_header_unique_number,
                                );
                                setShowLoadDetails(true);
                              }}
                            >
                              <span className="text-lg text-semi-bold text-hover-underline text-blue cursor-pointer">
                                {loadObj?.vehicle_tracking_load_code}
                              </span>
                            </button>
                            <span className="text-lg text-semi-bold text-grey">
                              is locked
                            </span>
                          </div>
                          <span className="loading-locked-subtitle">
                            To unlock this load complete the process of previous
                            load.
                          </span>
                        </div>
                      )}
                      <div className="loading-image-container">
                        <LoadingImage className="loading-icon-size" />
                      </div>
                      <div className="div-flex-column rg-5">
                        <div className="div-flex-row div-space-between div-align-center">
                          <div className="div-flex-row div-align-center cg-5">
                            <button
                              className="icon-btn-none"
                              aria-label="Camera Report"
                              title="Camera Report"
                              onClick={() =>
                                handleLoadCameraReportClick(loadObj)
                              }
                            >
                              <PrintIcon
                                color={getColorCode(themeColor)}
                                className="icon-size-xxxl"
                              />
                            </button>
                            <button
                              className="icon-btn-none"
                              onClick={() => {
                                setVtLoadHeaderUniqueNumber(
                                  loadObj?.vt_load_header_unique_number,
                                );
                                setShowLoadDetails(true);
                              }}
                            >
                              <span className="text-lg text-semi-bold text-hover-underline text-blue cursor-pointer">
                                {loadObj?.vehicle_tracking_load_code}
                              </span>
                            </button>
                          </div>
                          {loadObj?.completed ? (
                            <span className="text-sm status-pill green">
                              Completed
                            </span>
                          ) : loadObj?.is_active ? (
                            <span className="text-sm status-pill yellow">
                              Active
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                      </div>
                      <div className="border-bottom" />
                      <div className="div-flex-column rg-5">
                        <span className="text-sm text-grey">Tare weight</span>
                        <span className="text-sm text-semi-bold">
                          {loadObj?.actual_tare_weight}{' '}
                          {loadObj?.actual_tare_weight_uom}
                        </span>
                      </div>
                      <div className="div-flex-column rg-5">
                        <div className="div-flex-row div-space-between">
                          <span className="text-sm text-grey">Load weight</span>
                          <div className="div-flex-row cg-5 div-align-center">
                            <TargetInfoIcon className="icon-size-sm" />
                            <span className="text-sm text-grey">
                              Planned: {loadObj?.planned_load_gross_weight}
                            </span>
                          </div>
                        </div>
                        <div className="div-flex-row div-space-between cg-5 div-align-center">
                          <span className="text-sm text-semi-bold">
                            {loadObj?.completed
                              ? `${loadObj?.actual_load_gross_weight ?? '--'} ${
                                  loadObj?.actual_load_gross_weight_uom || ''
                                }`
                              : '--'}
                          </span>

                          {loadObj?.completed ? (
                            <VariancePill
                              value={calcVariance(
                                loadObj?.planned_load_gross_weight,
                                loadObj?.actual_load_gross_weight,
                              )}
                            />
                          ) : (
                            <span className="text-sm status-pill yellow">
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="div-flex-column rg-5">
                        <div className="div-flex-row div-space-between">
                          <span className="text-sm text-grey">
                            Gross weight
                          </span>
                          <div className="div-flex-row cg-5 div-align-center">
                            <TargetInfoIcon className="icon-size-sm" />
                            <span className="text-sm text-grey">
                              Planned: {loadObj?.planned_gross_weight}
                            </span>
                          </div>
                        </div>
                        <div className="div-flex-row div-space-between cg-5 div-align-center">
                          <span className="text-sm text-semi-bold">
                            {loadObj?.completed
                              ? `${loadObj?.actual_gross_weight ?? '--'} ${
                                  loadObj?.actual_gross_weight_uom || ''
                                }`
                              : '--'}
                          </span>
                          {loadObj?.completed ? (
                            <VariancePill
                              value={calcVariance(
                                loadObj?.planned_gross_weight,
                                loadObj?.actual_gross_weight,
                              )}
                            />
                          ) : (
                            <span className="text-sm status-pill yellow">
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                      {requestedStageEntry && (
                        <div className="div-flex-row div-space-between div-align-center">
                          <div className="div-flex-row div-align-center">
                            {canEditConfirmedLoad && (
                              <button
                                className={`common-btn-outlined theme-border-color-${themeColor} w-100 cursor-pointer`}
                                onClick={() => handleEditLoadClick(loadObj)}
                                disabled={loadObj?.is_locked}
                              >
                                <span className="text-sm text-semi-bold">
                                  Edit
                                </span>
                              </button>
                            )}
                          </div>
                          <div className="div-flex-row div-align-center">
                            <button
                              className={`common-btn theme-background-${themeColor}`}
                              onClick={() => handleProceedClick(loadObj)}
                              disabled={loadObj?.is_locked}
                            >
                              <span className="text-sm text-semi-bold">
                                {loadObj?.completed ? 'View' : 'Proceed'}
                              </span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <Row className="g-3">
              {vehicleTrackingLoadData?.map((loadObj) => (
                <Col xxl={2} xl={4} lg={6} key={loadObj?.id}>
                  <div className="">
                    <div className="loading-component-container div-flex-column rg-10">
                      <div className="loading-image-container">
                        <LoadingImage className="loading-icon-size" />
                      </div>
                      <div className="div-flex-column rg-5">
                        <div className="div-flex-row div-space-between div-align-center">
                          <div className="div-flex-row div-align-center cg-5">
                            <button
                              className="icon-btn-none"
                              aria-label="Camera Report"
                              title="Camera Report"
                              onClick={() =>
                                handleLoadCameraReportClick(loadObj)
                              }
                            >
                              <PrintIcon
                                color={getColorCode(themeColor)}
                                className="icon-size-xxxl"
                              />
                            </button>
                            <span className="text-lg text-semi-bold">
                              {loadObj?.vehicle_tracking_load_code}
                            </span>
                          </div>
                          {loadObj?.is_active && (
                            <span className="text-sm status-pill yellow ">
                              Active
                            </span>
                          )}
                        </div>
                        <div className="div-flex-column">
                          <span className="text-sm text-grey text-semi-bold">
                            {loadObj?.delivery_codes?.join(', ')}
                          </span>
                        </div>
                      </div>

                      {/* Weight summary */}
                      <div className="div-flex-column rg-5">
                        <div className="div-flex-row div-space-between">
                          <span className="text-sm text-grey">Tare Weight</span>
                          <span className="text-sm text-semi-bold">
                            {loadObj?.actual_tare_weight ?? '--'}{' '}
                            {loadObj?.actual_tare_weight_uom || ''}
                          </span>
                        </div>
                        <div className="div-flex-row div-space-between">
                          <span className="text-sm text-grey">Net Weight</span>
                          <span className="text-sm text-semi-bold">
                            {loadObj?.actual_load_gross_weight ?? '--'}{' '}
                            {loadObj?.actual_load_gross_weight_uom || ''}
                          </span>
                        </div>
                        <div className="div-flex-row div-space-between">
                          <span className="text-sm text-grey">
                            Gross Weight
                          </span>
                          <span className="text-sm text-semi-bold">
                            {loadObj?.actual_gross_weight ?? '--'}{' '}
                            {loadObj?.actual_gross_weight_uom || ''}
                          </span>
                        </div>
                      </div>

                      <div className="border-bottom" />
                      <div className="div-flex-row div-space-between div-align-center">
                        {canEditConfirmedLoad && (
                          <div className="div-flex-row">
                            <button
                              className={`common-btn-outlined theme-border-color-${themeColor} w-100 cursor-pointer`}
                              onClick={() => handleEditLoadClick(loadObj)}
                              disabled={loadObj?.is_locked}
                            >
                              <span className="text-sm text-semi-bold">
                                Edit
                              </span>
                            </button>
                          </div>
                        )}
                        <div className="div-flex-row">
                          <button
                            className={`common-btn theme-background-${themeColor}`}
                            onClick={() => {
                              setIsLoadEditing(false);
                              setVtLoadHeaderUniqueNumber(
                                loadObj?.vt_load_header_unique_number,
                              );
                              setShowLoadDetails(true);
                            }}
                          >
                            <span className="text-sm text-semi-bold">View</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
      <Drawer
        anchor="right"
        open={Boolean(showLoadDetails)}
        onClose={() => {
          setShowLoadDetails(false);
          setIsLoadEditing(false);
        }}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: 1 }}
        PaperProps={{ sx: { width: isMobile ? '100%' : '80%' } }}
      >
        <VehicleTrackingOutwardLoadDetails
          toggleLoadDetailDrawer={toggleLoadDetailDrawer}
          vtLoadHeaderUniqueNumber={vtLoadHeaderUniqueNumber}
          isLoadEditing={isLoadEditing}
          setIsLoadEditing={setIsLoadEditing}
        />
      </Drawer>
      <Modal
        show={showLoadCameraWeightReport}
        backdrop="static"
        keyboard={false}
        centered
        size="xl"
      >
        <div className="div-flex-column">
          <div className="div-flex-column rg-10">
            <div id="quotation-content">
              <PrintCameraWeightReport
                setShowCameraWeightReport={handleCloseLoadCameraReport}
                vtLoadHeaderUniqueNumber={cameraReportLoadHeaderUniqueNumber}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
